// ========== React & Libraries ==========
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { ChevronDown } from 'lucide-react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// ========== Commons ==========
import { getCoachesSortableColumnSchema, CoachMissingZoomLicense } from '@airreading/common/data';

// ========== Utils & Helpers ==========
import { EmptyArray, getCoachesMissingZoomLicense, updateCoachProfile } from '@/lib/api-calls';
import { validateRouteRole } from '@/lib/routing';
import { usePaginatedFilters } from '@/lib/filtering';
import { airServerErrorFrom, useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { getCoachStatusLabel } from '@/lib/coach-utils';
import { airColumnHelper } from '@/lib/tables';

// ========== UI Components ==========
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ========== Table Components ==========
import { EntityTable, createTableRowSelectionCell } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';

// ========== Layout Components ==========
import { canCreateUsers } from '@/auth/roles';
import { useSession } from '@/auth/useSession';
import { InfoPage } from '@/components/info-page';

// ========== Users/Teachers Components ==========
import { CreateCoach } from '@/routes/_layout/teachers/-components/create-coach';
import { CreateCoachesByCsv } from '@/routes/_layout/-components/create-coaches-by-csv';
import { coachesSectionTabs } from '../-components/tabs';
import { formatInstant, FrequentTimeZones, getUpcomingSessions, isAirAdmin } from '@airreading/common';
import { CheckboxZoomLicense } from './-components/checkbox-zoom-license';
import { useToast } from '@/components/ui/hooks/use-toast';

const usersSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
});

export const Route = createFileRoute('/_layout/teachers/missing-zoom-license/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: UsersIndex,
  validateSearch: usersSearchSchema,
});

const columnHelper = airColumnHelper<CoachMissingZoomLicense>(getCoachesSortableColumnSchema);

const coachesMissingZoomLicenseTableColumns = [
  createTableRowSelectionCell(columnHelper),

  columnHelper.accessor('fullNameOrEmail', {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link to={'/teachers/$teacherId'} params={{ teacherId: row.original.id }}>
        {getValue()}
      </Link>
    ),
  }),

  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
  }),

  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return getCoachStatusLabel(status);
    },
  }),

  columnHelper.accessor('studentGroupCount', {
    id: 'studentGroupCount',
    header: 'Number of active classes',
  }),

  columnHelper.accessor('sessions', {
    id: 'nextSession',
    header: 'Next session date',
    cell: ({ getValue }) => {
      const { nextSession } = getUpcomingSessions({
        sessions: getValue() ?? [],
        timeZone: FrequentTimeZones.UsCentral,
      });
      return nextSession && formatInstant(nextSession.startInstant, FrequentTimeZones.UsCentral).formattedDate;
    },
  }),

  columnHelper.accessor('zoomPersonalMeetingUrl', {
    id: 'zoomPersonalMeetingUrl',
    header: 'Zoom link',
  }),

  columnHelper.accessor('zoomAccountId', {
    id: 'zoomAccountId',
    header: 'Zoom ID',
  }),

  columnHelper.accessor((row) => row, {
    id: 'zoomType',
    header: 'License checkbox',
    cell: ({ getValue }) => {
      const coach = getValue();

      return <CheckboxZoomLicense {...coach} />;
    },
  }),
];

function UsersIndex() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { actingUser } = useSession();

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query']);
  const { mutateAsync: updateCoachProfileMutate } = useMutation({ mutationFn: updateCoachProfile });

  const { data, isPending, error, refetch } = useQuery(
    getCoachesMissingZoomLicense({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
      },
    }),
  );
  const coaches = useMemo(() => data?.coaches || EmptyArray, [data]);

  useErrorToast(error, 'Error loading teachers');

  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data: coaches,
    columns: coachesMissingZoomLicenseTableColumns,
    state: {
      pagination: { pageIndex, pageSize },
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
  });

  const filterComponents = (
    <>
      <TableFilterInputField
        placeholder="Search name or email"
        value={nameQuery ?? ''}
        onChange={(e) => setNameQuery(e.target.value)}
      />
      <TableFilterResetButton show={true} onClick={resetFilters} />
    </>
  );

  const onBulkCheckZoomLicense = async () => {
    const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);

    try {
      const promises = selectedRows.map(
        async ({
          id,
          JPSGoogleMeetUrl,
          zoomPersonalMeetingUrl,
          airReadingGoogleMeetUrl,
          zoomPersonalMeetingId,
          zoomType,
          zoomTechCheck,
          googleMeetTechCheck,
          classEligibilities,
          backgroundCheckStatuses,
        }) =>
          await updateCoachProfileMutate({
            userId: id,
            data: {
              zoomPersonalMeetingUrl: zoomPersonalMeetingUrl ?? undefined,
              zoomPersonalMeetingId: zoomPersonalMeetingId ?? undefined,
              zoomType: zoomType ?? undefined,
              JPSGoogleMeetUrl: JPSGoogleMeetUrl ?? undefined,
              airReadingGoogleMeetUrl: airReadingGoogleMeetUrl ?? undefined,
              zoomTechCheck: zoomTechCheck ?? undefined,
              googleMeetTechCheck: googleMeetTechCheck ?? undefined,
              backgroundCheckStatuses: backgroundCheckStatuses ?? [],
              classEligibilities: classEligibilities ?? [],
            },
          }),
      );

      await Promise.all(promises);

      queryClient.invalidateQueries(getCoachesMissingZoomLicense());
    } catch (e) {
      toast({
        title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to update zoom license',
      });
    }
  };

  const bulkActions = <DropdownMenuItem onClick={onBulkCheckZoomLicense}>Check Zoom license</DropdownMenuItem>;

  const createUsersMenu = canCreateUsers(actingUser) && (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add teachers
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateCoach
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a teacher</DropdownMenuItem>}
        />
        <CreateCoachesByCsv
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
          from="missing-zoom"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        autoManagedColumnIds={['selection']}
        hasError={!!error}
        emptyState="No users found"
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents: filterComponents }}
        bulkActionsMenuItems={bulkActions}
        topBarRightComponents={createUsersMenu}
      />
    </InfoPage>
  );
}
